import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import Breadcrumb from "components/Breadcrumb";
import Button from "components/Button";
import DeletedAlert from "components/DeletedAlert";
import { TitleArea, FooterArea, ContentArea as BaseContentArea, FormRow, Board } from "components/LayoutParts";
import TextForm from "components/TextForm";
import UploadForm from "components/UploadForm";
import UserModal, { UserModalConfig } from "components/UserModal";
import color from "constants/color";
import font from "constants/font";
import { useDebounce } from "hooks/useDebounce";
import { useUploadAimoParkingFile } from "pages/AimoParking/api";
import {
  useCreateCompany,
  useFindAllCompany,
  useFindCompanyById,
  useUpdateCompany,
  useRemoveCompany,
} from "pages/AimoParking/Company/api";
import AimoParkingSearchableSelect from "pages/AimoParking/component/AimoParkingSearchableSelect";
import {
  DELETE_MODAL_CONFIG,
  FAILED_MODAL_CONFIG,
  INITIAL_MODAL_CONFIG,
  SUCCEED_MODAL_CONFIG,
} from "pages/AimoParking/modalTemplate";
import { detectIsVendor } from "pages/AimoParking/utils/detectIsVendorUtil";
import { dateFormat } from "utils/dateTimeUtils";
import { getGCSPathFromSignedURL } from "utils/imageFileUtil";

// パーツ定義
const CancelWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;
const Wrap = styled.div`
  margin-top: 10px;
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  flex: 1;
`;
const ContentWrap = styled.div`
  gap: 16px;
`;
const ContentArea = styled(BaseContentArea)`
  display: flex;
`;
const SaveWrap = styled(CancelWrap)`
  align-items: center;
  justify-content: flex-end;
`;
const Annotation = styled.span`
  font-size: ${font.size12};
  color: ${color.text};
  margin-right: 16px;
`;
const Label = styled.div<{ top?: boolean }>`
  min-width: 200px;
  margin: 0 5px;
  padding-right: 18px;
  align-self: ${({ top }) => (top ? "flex-start" : "center")};
  margin-top: ${({ top }) => (top ? "14px" : "0")};
`;
const CurrentPageWrap = styled.p`
  font-size: ${font.size24};
  font-weight: bold;
  line-height: 40px;
`;
const UploadWrap = styled.div`
  width: 520px;
`;
const DeleteWrap = styled.div`
  display: flex;
  flex: auto;
  justify-content: flex-end;
`;
const SbPaymentTitle = styled.div`
  font-size: ${font.size16};
  color: ${color.text};
  margin: 32px 0 16px 4px;
`;
const IpWhitelistContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const IpWhitelistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const AddIpButton = styled.button`
  background: none;
  border: none;
  color: ${color.text.link};
  font-size: ${font.size14};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: ${color.text.disabled};
    cursor: not-allowed;
  }
`;
const RemoveIpButton = styled.button`
  background: none;
  border: none;
  color: ${color.text.link};
  cursor: pointer;
  padding: 4px;

  &:hover {
    background-color: ${color.form.disabledBackground};
    border-radius: 4px;
  }
`;

const UpsertCompany = () => {
  // フォームの値
  const [name, setName] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [logo, setLogo] = useState<Image[]>([]);
  const [merchantId, setMerchantId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [hashKey, setHashKey] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [subDomain, setSubDomain] = useState("");
  const [ipWhitelists, setIpWhitelists] = useState<string[]>([""]);
  const [parentCompany, setParentCompany] = useState<Choice | null>(null);
  const [modalConfig, setModalConfig] = useState<UserModalConfig>(INITIAL_MODAL_CONFIG);
  const [isVisible, setIsVisible] = useState(false);
  const [searchWord, setSearchWord] = useState("");
  const [pageIndex, setPageIndex] = useState(1);

  const { id: companyId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!companyId;

  // API hooks
  const { create } = useCreateCompany();
  const { update } = useUpdateCompany();
  const { remove } = useRemoveCompany();
  const { data: company } = useFindCompanyById({ id: companyId as string, revalidateOnFocus: false });
  const { uploadFiles } = useUploadAimoParkingFile({ uploadDestination: "company" });
  const { debouncedValue: debouncedSearchKeyword } = useDebounce(searchWord, 500);
  const {
    data: companyList,
    isValidating,
    pageInfo,
  } = useFindAllCompany({ keyword: debouncedSearchKeyword, pageIndex, pageSize: 50 });

  // フォーム変更ハンドラー
  const changeName = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value);
  const changeMerchantId = (e: React.ChangeEvent<HTMLInputElement>) => setMerchantId(e.target.value);
  const changeServiceId = (e: React.ChangeEvent<HTMLInputElement>) => setServiceId(e.target.value);
  const changeHashKey = (e: React.ChangeEvent<HTMLInputElement>) => setHashKey(e.target.value);
  const changeInvoiceNumber = (e: React.ChangeEvent<HTMLInputElement>) => setInvoiceNumber(e.target.value);
  const changeSubDomain = (e: React.ChangeEvent<HTMLInputElement>) => setSubDomain(e.target.value);
  const changeIpWhitelist = (index: number, value: string) => {
    const newIpWhitelists = [...ipWhitelists];
    newIpWhitelists[index] = value;
    setIpWhitelists(newIpWhitelists);
  };

  const addIpWhitelist = () => {
    if (ipWhitelists.length < 10) {
      setIpWhitelists([...ipWhitelists, ""]);
    }
  };

  const removeIpWhitelist = (index: number) => {
    // 最後の1つのフィールドは削除できない
    if (ipWhitelists.length <= 1) return;
    const newIpWhitelists = ipWhitelists.filter((_, i) => i !== index);
    setIpWhitelists(newIpWhitelists);
  };

  // IPアドレスのバリデーション関数
  const isValidIpAddress = (ip: string): boolean => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  // バリデーション
  const isVendor = isEditMode ? detectIsVendor(company?.vendors) : !!parentCompany?.value;
  const isErrorName = name === "";
  const isErrorLogo = isEditMode ? images.length === 0 && logo.length === 0 : images.length === 0;
  const isErrorMerchantId = isVendor && merchantId.length === 0;
  const isErrorServiceId = isVendor && serviceId.length === 0;
  const isErrorHashKey = isVendor && hashKey.length === 0;
  const isErrorInvoiceNumber = !/^T\d{13}$/.test(invoiceNumber);

  // IPホワイトリストのバリデーション（空でないもののみチェック）
  const validIpWhitelists = ipWhitelists.filter((ip) => ip.trim().length > 0);
  const isErrorIpWhitelists = validIpWhitelists.some((ip) => !isValidIpAddress(ip));

  // サブドメインとIPホワイトリストの相互依存バリデーション
  const hasSubDomain = subDomain.trim().length > 0;
  const hasIpWhitelists = validIpWhitelists.length > 0;
  const isErrorSubDomainRequired = hasIpWhitelists && !hasSubDomain;
  const isErrorIpWhitelistsRequired = hasSubDomain && !hasIpWhitelists;

  const isError =
    isErrorName ||
    isErrorInvoiceNumber ||
    isErrorLogo ||
    isErrorMerchantId ||
    isErrorServiceId ||
    isErrorHashKey ||
    isErrorIpWhitelists ||
    isErrorSubDomainRequired ||
    isErrorIpWhitelistsRequired;

  // 編集モードの場合の変更検知
  const isChanged = isEditMode
    ? name !== company?.name ||
      invoiceNumber !== company?.invoiceNumber ||
      logo[0]?.url !== company?.logo ||
      merchantId !== (company?.paymentInfo?.merchantId || "") ||
      serviceId !== (company?.paymentInfo?.serviceId || "") ||
      hashKey !== (company?.paymentInfo?.hashKey || "") ||
      subDomain !== (company?.subDomain || "") ||
      JSON.stringify(validIpWhitelists) !== JSON.stringify(company?.ipWhitelist || [])
    : true;

  const canSubmit = !isError && (isEditMode ? isChanged && !company?.deletedAt : true);

  const onSubmit = useCallback(async () => {
    const uploadImageResponse = await uploadFiles({ files: images }).catch((e) => {
      setModalConfig(FAILED_MODAL_CONFIG({ onClose: () => setIsVisible(false) }));
      setIsVisible(true);
      throw e;
    });

    if (!uploadImageResponse) return;

    if (isEditMode) {
      // 編集モード
      if (!companyId) return;
      const response = await update(companyId, {
        id: companyId,
        invoiceNumber,
        logo: uploadImageResponse[0] ?? getGCSPathFromSignedURL(logo[0].url),
        name,
        paymentInfo: merchantId && serviceId && hashKey ? { merchantId, serviceId, hashKey } : undefined,
        subDomain: subDomain || undefined,
        ipWhitelist: validIpWhitelists.length > 0 ? validIpWhitelists : undefined,
      }).catch((e) => {
        setModalConfig(FAILED_MODAL_CONFIG({ onClose: () => setIsVisible(false) }));
        setIsVisible(true);
        throw e;
      });

      if (response.status === 200) {
        setModalConfig(
          SUCCEED_MODAL_CONFIG({ message: "変更内容を保存しました", onClose: () => navigate("/aimo_parking/company") })
        );
        setIsVisible(true);
      }
    } else {
      // 作成モード
      const response = await create({
        invoiceNumber,
        logo: uploadImageResponse[0],
        name,
        paymentInfo: {
          hashKey,
          merchantId,
          serviceId,
        },
        subDomain: subDomain || undefined,
        ipWhitelist: validIpWhitelists.length > 0 ? validIpWhitelists : undefined,
        parentCompanyId: parentCompany?.value ?? undefined,
      }).catch((e) => {
        setModalConfig(FAILED_MODAL_CONFIG({ onClose: () => setIsVisible(false) }));
        setIsVisible(true);
        throw e;
      });

      if (response.status === 201) {
        setModalConfig(
          SUCCEED_MODAL_CONFIG({ message: "追加しました", onClose: () => navigate("/aimo_parking/company") })
        );
        setIsVisible(true);
      }
    }
  }, [
    isEditMode,
    companyId,
    uploadFiles,
    images,
    logo,
    update,
    create,
    invoiceNumber,
    name,
    merchantId,
    serviceId,
    hashKey,
    subDomain,
    validIpWhitelists,
    parentCompany?.value,
    navigate,
  ]);

  const handleRemoveCompany = async () => {
    if (!companyId) return;
    const response = await remove(companyId).catch((e) => {
      setModalConfig(FAILED_MODAL_CONFIG({ onClose: () => setIsVisible(false) }));
      setIsVisible(true);
      throw e;
    });
    if (response.status === 200) {
      setModalConfig(
        SUCCEED_MODAL_CONFIG({ message: "削除しました", onClose: () => navigate("/aimo_parking/company") })
      );
      setIsVisible(true);
    }
  };

  const handleClickRemove = async () => {
    setModalConfig(DELETE_MODAL_CONFIG({ positive: handleRemoveCompany, negative: () => setIsVisible(false) }));
    setIsVisible(true);
  };

  const setUp = useCallback(() => {
    if (isEditMode && images.length === 1) return;
    if (isEditMode && company) {
      setName(company.name);
      setMerchantId(company.paymentInfo?.merchantId ?? "");
      setServiceId(company.paymentInfo?.serviceId ?? "");
      setHashKey(company.paymentInfo?.hashKey ?? "");
      setInvoiceNumber(company.invoiceNumber);
      setLogo([{ url: company.logo }]);
      setSubDomain(company.subDomain || "");
      // IPホワイトリストの初期値設定（既存データがある場合）
      if (company.ipWhitelist && company.ipWhitelist.length > 0) {
        setIpWhitelists(company.ipWhitelist);
      } else {
        setIpWhitelists([""]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, company]);

  useEffect(() => {
    setUp();
  }, [setUp]);

  if (isEditMode && !company) return null;

  return (
    <Board>
      <TitleArea>
        {isEditMode ? (
          <>
            <Breadcrumb
              currentPageName="会社情報を編集"
              breadcrumbItems={[
                { pageName: `${company?.name}`, onClick: () => navigate(`/aimo_parking/company/${company?.id}`) },
              ]}
            />
            {!company?.deletedAt && (
              <DeleteWrap>
                <Button label="この管理会社を削除" onClick={handleClickRemove} type="danger" />
              </DeleteWrap>
            )}
          </>
        ) : (
          <CurrentPageWrap>管理会社を追加</CurrentPageWrap>
        )}
      </TitleArea>
      {isEditMode && company?.deletedAt && (
        <DeletedAlert message={`この管理会社は削除済みです。 削除日: ${dateFormat(company?.deletedAt)}`} />
      )}
      <Wrap>
        <ContentWrap>
          <ContentArea>
            <FormRow>
              <Label>会社名</Label>
              <TextForm
                type="text"
                width="832px"
                value={name}
                placeholder="例）ランドイット不動産"
                required
                onChange={changeName}
                errorMessage="会社名を入力してください"
                isError={isErrorName}
              />
            </FormRow>
            <FormRow>
              <Label top>ロゴ</Label>
              <UploadWrap>
                <UploadForm
                  unUploadedImages={images}
                  setUnUploadedImages={setImages}
                  showImages={isEditMode ? logo : undefined}
                  setShowImages={isEditMode ? setLogo : undefined}
                  single
                />
              </UploadWrap>
            </FormRow>
            <FormRow>
              <Label>企業インボイス番号</Label>
              <TextForm
                type="text"
                width="350px"
                value={invoiceNumber}
                placeholder="例）T0123456789012"
                required
                onChange={changeInvoiceNumber}
                errorMessage="T以後13桁の英数字で入力して下さい"
                isError={isErrorInvoiceNumber}
              />
            </FormRow>
            <FormRow>
              <Label>サブドメイン</Label>
              <TextForm
                type="text"
                width="350px"
                value={subDomain}
                placeholder="例）company-a"
                onChange={changeSubDomain}
                errorMessage="IPホワイトリストが入力されている場合は必須です"
                isError={isErrorSubDomainRequired}
              />
            </FormRow>
            <FormRow>
              <Label>IPホワイトリスト</Label>
              <IpWhitelistContainer>
                {ipWhitelists.map((ip, index) => (
                  <IpWhitelistItem key={index}>
                    <TextForm
                      type="text"
                      width="300px"
                      value={ip}
                      placeholder="例）192.168.1.1"
                      onChange={(e) => changeIpWhitelist(index, e.target.value)}
                      errorMessage={
                        ip.trim().length > 0 && !isValidIpAddress(ip) ? "有効なIPv4アドレスを入力してください" : ""
                      }
                      isError={ip.trim().length > 0 && !isValidIpAddress(ip)}
                    />
                    {ipWhitelists.length > 1 && (
                      <RemoveIpButton type="button" onClick={() => removeIpWhitelist(index)} title="削除">
                        ✕ 削除
                      </RemoveIpButton>
                    )}
                  </IpWhitelistItem>
                ))}
                <AddIpButton type="button" onClick={addIpWhitelist} disabled={ipWhitelists.length >= 10}>
                  + IPアドレスを追加
                </AddIpButton>
                {isErrorIpWhitelistsRequired && (
                  <div style={{ color: color.attention, fontSize: "12px", marginTop: "4px" }}>
                    サブドメインが入力されている場合は必須です
                  </div>
                )}
              </IpWhitelistContainer>
            </FormRow>
            {isVendor && (
              <>
                <SbPaymentTitle>SBペイメント情報</SbPaymentTitle>
                <FormRow>
                  <Label>企業識別ID</Label>
                  <TextForm
                    type="text"
                    width="350px"
                    value={merchantId}
                    placeholder="例）h8Xz3bW9v2F7Yk4mNpQ6L1VrTs5A8G0d"
                    required
                    onChange={changeMerchantId}
                    errorMessage="企業識別IDを入力してください"
                    isError={isErrorMerchantId}
                  />
                </FormRow>
                <FormRow>
                  <Label>サービス識別ID</Label>
                  <TextForm
                    type="text"
                    width="350px"
                    value={serviceId}
                    placeholder="例）h8Xz3bW9v2F7Yk4mNpQ6L1VrTs5A8G0d"
                    required
                    onChange={changeServiceId}
                    errorMessage="サービス識別IDを入力してください"
                    isError={isErrorServiceId}
                  />
                </FormRow>
                <FormRow>
                  <Label>ハッシュキー</Label>
                  <TextForm
                    type="text"
                    width="350px"
                    value={hashKey}
                    placeholder="例）h8Xz3bW9v2F7Yk4mNpQ6L1VrTs5A8G0d"
                    required
                    onChange={changeHashKey}
                    errorMessage="ハッシュキーを入力してください"
                    isError={isErrorHashKey}
                  />
                </FormRow>
              </>
            )}
          </ContentArea>
          {!isEditMode && (
            <ContentArea>
              <FormRow>
                <Label>販売元企業</Label>
                <AimoParkingSearchableSelect
                  choices={companyList?.map((company) => ({ label: company.name, value: company.id })) ?? []}
                  onChange={(v) => setParentCompany(v)}
                  width="380px"
                  placeHolder="選択してください（任意）"
                  search={searchWord}
                  setSearch={setSearchWord}
                  validating={isValidating}
                  hasMore={pageInfo?.hasMore}
                  setPageIndex={setPageIndex}
                />
              </FormRow>
            </ContentArea>
          )}
        </ContentWrap>
        <FooterArea>
          <CancelWrap>
            <Button
              type="secondary"
              onClick={() => navigate(isEditMode ? `/aimo_parking/company/${companyId}` : "/aimo_parking/company")}
              label="キャンセル"
              width="160px"
            />
          </CancelWrap>
          <SaveWrap>
            <Annotation>
              {isEditMode ? "保存するまで変更内容は反映されません" : "追加するまで変更内容は反映されません"}
            </Annotation>
            <Button
              type="primary"
              onClick={onSubmit}
              label={isEditMode ? "変更内容を保存" : "この管理会社を追加"}
              disabled={!canSubmit}
              width="160px"
            />
          </SaveWrap>
        </FooterArea>
      </Wrap>
      <UserModal isVisible={isVisible} config={modalConfig} />
    </Board>
  );
};

export default UpsertCompany;
